import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { notifySuccess } from "../../../../components/AppToast";
import { fieldError, staffApiError } from "../apiErrors";
import { useInviteStaff } from "../hooks";
import { StaffRolesSelect, optionsToRoles } from "./StaffRolesSelect";

const HEADING_ID = "invite-staff-modal-title";
const FORM_ID = "invite-staff-form";

/**
 * Alta de un miembro del equipo interno (PLATFORM_ADMIN).
 *
 * El backend crea la cuenta con una contraseña temporal y `must_change_password`
 * y la envía por correo; no es el flujo de activación de allauth.
 *
 * A11y: `aria-labelledby` + `aria-modal` y foco inicial en el email
 * (`onEntered`). El focus trap lo aporta react-bootstrap de forma nativa.
 */
function InviteStaffModal({ show, onHide }) {
  const [form, setForm] = useState({ email: "", name: "" });
  const [roles, setRoles] = useState([]);
  const [rolesError, setRolesError] = useState(null);
  const emailRef = useRef(null);
  const invite = useInviteStaff();

  useEffect(() => {
    if (show) {
      setForm({ email: "", name: "" });
      setRoles([]);
      setRolesError(null);
      invite.reset();
    }
    // `invite` cambia de identidad en cada render de react-query; solo nos
    // interesa reaccionar a la apertura del modal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  function patch(field) {
    return (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    // react-select no admite el `required` de HTML5 → validación imperativa.
    if (roles.length === 0) {
      setRolesError("Selecciona al menos un rol.");
      return;
    }
    setRolesError(null);
    invite.mutate(
      { ...form, roles: optionsToRoles(roles) },
      {
        onSuccess: (member) => {
          notifySuccess(`Invitación enviada a ${member?.email ?? form.email}.`);
          onHide();
        },
      }
    );
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      aria-labelledby={HEADING_ID}
      aria-modal="true"
      onEntered={() => emailRef.current?.focus()}
    >
      <Modal.Header closeButton>
        <Modal.Title id={HEADING_ID}>Invitar miembro del equipo</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {invite.isError && (
          <Alert variant="danger" className="small">
            {staffApiError(invite.error, "No se pudo enviar la invitación.")}
          </Alert>
        )}
        <Form id={FORM_ID} onSubmit={handleSubmit}>
          <Form.Group className="mb-3" controlId="staff-invite-email">
            <Form.Label>Correo</Form.Label>
            <Form.Control
              ref={emailRef}
              type="email"
              required
              value={form.email}
              onChange={patch("email")}
              aria-label="Correo del miembro de staff"
              isInvalid={Boolean(fieldError(invite.error, "email"))}
            />
            <Form.Control.Feedback type="invalid">
              {fieldError(invite.error, "email")}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3" controlId="staff-invite-name">
            <Form.Label>Nombre</Form.Label>
            <Form.Control
              required
              maxLength={255}
              value={form.name}
              onChange={patch("name")}
              aria-label="Nombre del miembro de staff"
              isInvalid={Boolean(fieldError(invite.error, "name"))}
            />
            <Form.Control.Feedback type="invalid">
              {fieldError(invite.error, "name")}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-2" controlId="staff-invite-roles">
            <Form.Label>Roles</Form.Label>
            <StaffRolesSelect
              inputId="staff-invite-roles"
              value={roles}
              onChange={setRoles}
              isDisabled={invite.isPending}
            />
            {(rolesError || fieldError(invite.error, "roles")) && (
              <Form.Text className="text-danger">
                {rolesError ?? fieldError(invite.error, "roles")}
              </Form.Text>
            )}
          </Form.Group>
          <Form.Text className="text-muted">
            Recibirá un correo con una contraseña temporal que deberá cambiar al
            entrar.
          </Form.Text>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={invite.isPending}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          type="submit"
          form={FORM_ID}
          disabled={invite.isPending}
          aria-label="Confirmar invitación de staff"
        >
          {invite.isPending ? <Spinner size="sm" aria-hidden="true" /> : "Invitar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export { InviteStaffModal };
